import { SKILLS, type SkillKey } from '@shared/rules/constants'
import {
  EMPTY_FEAT_SELECTION, magicInitiateList, type CharacterDraft, type FeatSelection,
} from '@shared/rules/build'
import { CLASS_BUILDS_BY_ID } from '@shared/rules/data/classes'
import { SPECIES_BY_ID } from '@shared/rules/data/species'
import { BACKGROUNDS_BY_ID } from '@shared/rules/data/backgrounds'
import { FEATS_BY_ID, ORIGIN_FEATS } from '@shared/rules/data/feats'
import { spellsFor } from '@shared/rules/data/spells'
import { Panel } from '@/components/ui'
import { ChoiceCard, Explainer, PickChip, PickCounter, SectionLabel, StepIntro } from './shared'
import type { StepProps } from './StepIdentity'

export function StepFeatures({ draft, update }: StepProps) {
  const classBuild = draft.classId ? CLASS_BUILDS_BY_ID.get(draft.classId) : null
  const species = SPECIES_BY_ID.get(draft.speciesId)
  const background = BACKGROUNDS_BY_ID.get(draft.backgroundId)
  if (!classBuild || !species || !background) return null

  const classChoices = classBuild.choices.filter((choice) => choice.id !== 'expertise')

  const toggleClassChoice = (choiceId: string, value: string, count: number) => {
    const current = draft.classChoices[choiceId] ?? []
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : count === 1
        ? [value]
        : current.length < count
          ? [...current, value]
          : current
    update({ classChoices: { ...draft.classChoices, [choiceId]: next } })
  }

  return (
    <div className="space-y-6">
      <StepIntro title="Your special abilities">
        These are the perks that make your character feel different from anyone else with the same
        class. A few of them ask you to make one more choice.
      </StepIntro>

      <Panel className="p-4">
        <SectionLabel>What every {classBuild.name} gets</SectionLabel>
        <ul className="space-y-2.5">
          {classBuild.level1Features.map((entry) => (
            <li key={entry.name} className="text-sm">
              <span className="font-medium text-ink-100">{entry.name}. </span>
              <span className="text-ink-400">{entry.text}</span>
            </li>
          ))}
        </ul>
      </Panel>

      {classChoices.map((choice) => {
        const picked = draft.classChoices[choice.id] ?? []
        return (
          <Panel key={choice.id} className="p-4">
            <SectionLabel action={<PickCounter chosen={picked.length} total={choice.count} noun="chosen" />}>
              {choice.label}
            </SectionLabel>
            <p className="mb-3 text-sm text-ink-400">{choice.help}</p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {choice.options.map((option) => (
                <ChoiceCard
                  key={option.value}
                  selected={picked.includes(option.value)}
                  onSelect={() => toggleClassChoice(choice.id, option.value, choice.count)}
                  disabled={!picked.includes(option.value) && picked.length >= choice.count}
                  title={option.label}
                  tagline={option.help}
                />
              ))}
            </div>
          </Panel>
        )
      })}

      <FeatBlock
        title={`${background.name} feat`}
        selection={draft.backgroundFeat}
        onChange={(selection) => update({ backgroundFeat: selection })}
        lockedFeatId={background.featId}
      />

      {species.grantsOriginFeat && (
        <FeatBlock
          title={`${species.name} feat`}
          intro="Humans get an extra Origin feat of their choice on top of the one from their background."
          selection={draft.speciesFeat ?? { ...EMPTY_FEAT_SELECTION }}
          onChange={(selection) => update({ speciesFeat: selection })}
          excludeFeatId={draft.backgroundFeat.featId}
        />
      )}

      <Explainer term="feat">
        A feat is a permanent special ability. Unlike a spell you have to prepare or a resource you
        spend, a feat is simply always true about your character.
      </Explainer>
    </div>
  )
}

function FeatBlock({
  title, intro, selection, onChange, lockedFeatId, excludeFeatId,
}: {
  title: string
  intro?: string
  selection: FeatSelection
  onChange: (selection: FeatSelection) => void
  /** Background feats are fixed; this renders them read-only. */
  lockedFeatId?: string
  excludeFeatId?: string
}) {
  const feat = FEATS_BY_ID.get(selection.featId)
  const spellList = magicInitiateList(selection.featId)

  return (
    <Panel className="p-4">
      <SectionLabel>{title}</SectionLabel>
      {intro && <p className="mb-3 text-sm text-ink-400">{intro}</p>}

      {lockedFeatId ? (
        feat && (
          <div className="rounded-xl border border-gold-600/40 bg-gold-900/25 p-3">
            <h3 className="font-display text-base text-gold-400">{feat.name}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-ink-300">{feat.text}</p>
          </div>
        )
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {ORIGIN_FEATS.filter((f) => f.id !== excludeFeatId).map((option) => (
            <ChoiceCard
              key={option.id}
              selected={selection.featId === option.id}
              onSelect={() => onChange({ ...EMPTY_FEAT_SELECTION, featId: option.id })}
              title={option.name}
              tagline={option.tagline}
            >
              {option.text}
            </ChoiceCard>
          ))}
        </div>
      )}

      {spellList && (
        <div className="mt-4 space-y-4 border-t border-ink-700/60 pt-4">
          <div>
            <SectionLabel action={<PickCounter chosen={selection.cantrips.length} total={2} noun="cantrips" />}>
              Pick 2 {spellList} cantrips
            </SectionLabel>
            <p className="mb-2.5 text-sm text-ink-400">
              Cantrips are small spells you can cast as often as you like, forever.
            </p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {spellsFor(spellList, 0).map((spell) => (
                <PickChip
                  key={spell.id}
                  label={spell.name}
                  meta={spell.school}
                  help={spell.text}
                  selected={selection.cantrips.includes(spell.id)}
                  disabled={selection.cantrips.length >= 2}
                  onToggle={() => onChange({
                    ...selection,
                    cantrips: selection.cantrips.includes(spell.id)
                      ? selection.cantrips.filter((id) => id !== spell.id)
                      : [...selection.cantrips, spell.id],
                  })}
                />
              ))}
            </div>
          </div>

          <div>
            <SectionLabel action={<PickCounter chosen={selection.spell ? 1 : 0} total={1} noun="spell" />}>
              Pick 1 first-level {spellList} spell
            </SectionLabel>
            <p className="mb-2.5 text-sm text-ink-400">
              You can cast this once per long rest without using a spell slot.
            </p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {spellsFor(spellList, 1).map((spell) => (
                <PickChip
                  key={spell.id}
                  label={spell.name}
                  meta={spell.school}
                  help={spell.text}
                  selected={selection.spell === spell.id}
                  onToggle={() => onChange({
                    ...selection,
                    spell: selection.spell === spell.id ? '' : spell.id,
                  })}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {selection.featId === 'skilled' && (
        <div className="mt-4 border-t border-ink-700/60 pt-4">
          <SectionLabel action={<PickCounter chosen={selection.skills.length} total={3} noun="chosen" />}>
            Pick 3 skills
          </SectionLabel>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {(Object.keys(SKILLS) as SkillKey[]).map((skill) => (
              <PickChip
                key={skill}
                label={SKILLS[skill].name}
                meta={SKILLS[skill].ability}
                selected={selection.skills.includes(skill)}
                disabled={selection.skills.length >= 3}
                onToggle={() => onChange({
                  ...selection,
                  skills: selection.skills.includes(skill)
                    ? selection.skills.filter((s) => s !== skill)
                    : [...selection.skills, skill],
                })}
              />
            ))}
          </div>
        </div>
      )}

      {(selection.featId === 'crafter' || selection.featId === 'musician') && (
        <div className="mt-4 border-t border-ink-700/60 pt-4">
          <label className="block text-sm">
            <span className="mb-1.5 block text-xs uppercase tracking-wider text-ink-400">
              Which {selection.featId === 'crafter' ? 'three sets of tools' : 'three instruments'}?
            </span>
            <input
              value={selection.notes}
              onChange={(e) => onChange({ ...selection, notes: e.target.value })}
              placeholder={selection.featId === 'crafter'
                ? "Carpenter's, Smith's, Tinker's"
                : 'Lute, Drum, Pan flute'}
              maxLength={300}
              className="w-full rounded-xl border border-ink-600/80 bg-ink-900/70 px-3 py-2 text-sm text-ink-100 placeholder:text-ink-500 focus:border-gold-600 focus:outline-none"
            />
          </label>
        </div>
      )}
    </Panel>
  )
}

export function draftFeatSelections(draft: CharacterDraft): FeatSelection[] {
  return [draft.backgroundFeat, draft.speciesFeat].filter(
    (s): s is FeatSelection => Boolean(s?.featId),
  )
}
